export const essentialTools = {
  junior: {
    primary: [
      { name: 'Visual Studio Code', category: 'editor', description: 'Primary code editor with excellent JavaScript/React support. Install extensions: ES7+ React/Redux/React-Native snippets, Prettier, ESLint, Auto Rename Tag, Bracket Pair Colorizer.' },
      { name: 'Node.js', category: 'runtime', description: 'JavaScript runtime environment that allows you to run JavaScript outside the browser. Includes npm (Node Package Manager) for installing packages and dependencies.' },
      { name: 'Git', category: 'version-control', description: 'Distributed version control system for tracking changes in source code. Essential for collaborating with team members and managing code history.' },
      { name: 'Chrome DevTools', category: 'debugging', description: 'Built-in browser debugging tools for inspecting HTML, CSS, JavaScript. Learn to use Console, Elements, Network, and Sources tabs for effective debugging.' },
      { name: 'React Developer Tools', category: 'debugging', description: 'Browser extension for debugging React applications. Allows you to inspect React component hierarchy, props, and state in the browser.' },
      { name: 'npm/yarn', category: 'package-manager', description: 'Package managers for installing and managing JavaScript dependencies. npm comes with Node.js, yarn is an alternative with better performance.' }
    ],
    secondary: [
      { name: 'Figma', category: 'design', description: 'Design collaboration tool for viewing and implementing designs. Request access to design files from your design team.' },
      { name: 'Postman', category: 'api-testing', description: 'Tool for testing API endpoints. Useful for understanding how frontend communicates with backend services.' },
      { name: 'Slack', category: 'communication', description: 'Team communication platform. Join relevant channels: #frontend-team, #general, #help.' }
    ]
  },
  mid: {
    primary: [
      { name: 'Visual Studio Code', category: 'editor', description: 'Code editor with advanced extensions and configurations' },
      { name: 'Node.js', category: 'runtime', description: 'JavaScript runtime with npm package management' },
      { name: 'Git', category: 'version-control', description: 'Version control with advanced branching strategies' },
      { name: 'Chrome DevTools', category: 'debugging', description: 'Browser debugging with performance profiling' },
      { name: 'React Developer Tools', category: 'debugging', description: 'React debugging and performance monitoring' },
      { name: 'npm/yarn', category: 'package-manager', description: 'Package management with workspace configurations' },
      { name: 'Docker', category: 'containerization', description: 'Containerization for consistent development environments' },
      { name: 'Webpack/Vite', category: 'build-tools', description: 'Module bundlers for optimizing application builds' }
    ],
    secondary: [
      { name: 'Figma', category: 'design', description: 'Design collaboration with developer handoff features' },
      { name: 'Postman', category: 'api-testing', description: 'API testing with automated test suites' },
      { name: 'Jest/Testing Library', category: 'testing', description: 'Testing frameworks for unit and integration tests' },
      { name: 'Storybook', category: 'component-development', description: 'Tool for developing and testing UI components in isolation' }
    ]
  },
  senior: {
    primary: [
      { name: 'Visual Studio Code', category: 'editor', description: 'Advanced IDE setup' },
      { name: 'Node.js', category: 'runtime', description: 'Runtime management' },
      { name: 'Git', category: 'version-control', description: 'Advanced Git workflows' },
      { name: 'Chrome DevTools', category: 'debugging', description: 'Performance optimization' },
      { name: 'React Developer Tools', category: 'debugging', description: 'React profiling' },
      { name: 'npm/yarn', category: 'package-manager', description: 'Monorepo management' },
      { name: 'Docker', category: 'containerization', description: 'Container orchestration' },
      { name: 'Webpack/Vite', category: 'build-tools', description: 'Build optimization' },
      { name: 'Kubernetes', category: 'orchestration', description: 'Container orchestration' },
      { name: 'Terraform', category: 'infrastructure', description: 'Infrastructure as code' }
    ],
    secondary: [
      { name: 'Figma', category: 'design', description: 'Design system management' },
      { name: 'Postman', category: 'api-testing', description: 'API architecture testing' },
      { name: 'Jest/Testing Library', category: 'testing', description: 'Testing strategy' },
      { name: 'Storybook', category: 'component-development', description: 'Component architecture' },
      { name: 'Grafana', category: 'monitoring', description: 'Performance monitoring' },
      { name: 'Sentry', category: 'error-tracking', description: 'Error monitoring' }
    ]
  },
  commands: {
    macos: {
      junior: [
        { tool: 'VS Code', command: 'code .', description: 'Open current directory in VS Code. Use this to start working on projects.' },
        { tool: 'Node.js', command: 'node --version && npm --version', description: 'Check Node.js and npm versions to ensure proper installation.' },
        { tool: 'Git', command: 'git status', description: 'Check current repository status. Shows modified files, branch info, and staging area.' },
        { tool: 'npm', command: 'npm install', description: 'Install project dependencies listed in package.json. Run this in project root.' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start development server. Usually runs on http://localhost:3000.' }
      ],
      mid: [
        { tool: 'VS Code', command: 'code .', description: 'Open project in VS Code' },
        { tool: 'Node.js', command: 'node --version && npm --version', description: 'Verify Node.js installation' },
        { tool: 'Git', command: 'git status', description: 'Check repository status' },
        { tool: 'npm', command: 'npm install', description: 'Install dependencies' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start development server' },
        { tool: 'Docker', command: 'docker-compose up', description: 'Start containerized development environment' },
        { tool: 'Testing', command: 'npm test', description: 'Run test suite' }
      ],
      senior: [
        { tool: 'VS Code', command: 'code .', description: 'Open project' },
        { tool: 'Node.js', command: 'node --version', description: 'Check version' },
        { tool: 'Git', command: 'git status', description: 'Repository status' },
        { tool: 'npm', command: 'npm install', description: 'Install deps' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start server' },
        { tool: 'Docker', command: 'docker-compose up', description: 'Start containers' },
        { tool: 'Testing', command: 'npm test', description: 'Run tests' },
        { tool: 'Build', command: 'npm run build', description: 'Production build' },
        { tool: 'Kubernetes', command: 'kubectl get pods', description: 'Check pod status' }
      ]
    },
    windows: {
      junior: [
        { tool: 'VS Code', command: 'code .', description: 'Open current directory in VS Code. Use this to start working on projects.' },
        { tool: 'Node.js', command: 'node --version && npm --version', description: 'Check Node.js and npm versions to ensure proper installation.' },
        { tool: 'Git', command: 'git status', description: 'Check current repository status. Shows modified files, branch info, and staging area.' },
        { tool: 'npm', command: 'npm install', description: 'Install project dependencies listed in package.json. Run this in project root.' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start development server. Usually runs on http://localhost:3000.' }
      ],
      mid: [
        { tool: 'VS Code', command: 'code .', description: 'Open project in VS Code' },
        { tool: 'Node.js', command: 'node --version && npm --version', description: 'Verify Node.js installation' },
        { tool: 'Git', command: 'git status', description: 'Check repository status' },
        { tool: 'npm', command: 'npm install', description: 'Install dependencies' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start development server' },
        { tool: 'Docker', command: 'docker-compose up', description: 'Start containerized development environment' },
        { tool: 'Testing', command: 'npm test', description: 'Run test suite' }
      ],
      senior: [
        { tool: 'VS Code', command: 'code .', description: 'Open project' },
        { tool: 'Node.js', command: 'node --version', description: 'Check version' },
        { tool: 'Git', command: 'git status', description: 'Repository status' },
        { tool: 'npm', command: 'npm install', description: 'Install deps' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start server' },
        { tool: 'Docker', command: 'docker-compose up', description: 'Start containers' },
        { tool: 'Testing', command: 'npm test', description: 'Run tests' },
        { tool: 'Build', command: 'npm run build', description: 'Production build' },
        { tool: 'Kubernetes', command: 'kubectl get pods', description: 'Check pod status' }
      ]
    },
    linux: {
      junior: [
        { tool: 'VS Code', command: 'code .', description: 'Open current directory in VS Code. Use this to start working on projects.' },
        { tool: 'Node.js', command: 'node --version && npm --version', description: 'Check Node.js and npm versions to ensure proper installation.' },
        { tool: 'Git', command: 'git status', description: 'Check current repository status. Shows modified files, branch info, and staging area.' },
        { tool: 'npm', command: 'npm install', description: 'Install project dependencies listed in package.json. Run this in project root.' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start development server. Usually runs on http://localhost:3000.' }
      ],
      mid: [
        { tool: 'VS Code', command: 'code .', description: 'Open project in VS Code' },
        { tool: 'Node.js', command: 'node --version && npm --version', description: 'Verify Node.js installation' },
        { tool: 'Git', command: 'git status', description: 'Check repository status' },
        { tool: 'npm', command: 'npm install', description: 'Install dependencies' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start development server' },
        { tool: 'Docker', command: 'docker-compose up', description: 'Start containerized development environment' },
        { tool: 'Testing', command: 'npm test', description: 'Run test suite' }
      ],
      senior: [
        { tool: 'VS Code', command: 'code .', description: 'Open project' },
        { tool: 'Node.js', command: 'node --version', description: 'Check version' },
        { tool: 'Git', command: 'git status', description: 'Repository status' },
        { tool: 'npm', command: 'npm install', description: 'Install deps' },
        { tool: 'React Dev Server', command: 'npm start', description: 'Start server' },
        { tool: 'Docker', command: 'docker-compose up', description: 'Start containers' },
        { tool: 'Testing', command: 'npm test', description: 'Run tests' },
        { tool: 'Build', command: 'npm run build', description: 'Production build' },
        { tool: 'Kubernetes', command: 'kubectl get pods', description: 'Check pod status' }
      ]
    }
  }
};
